import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAdminAuth } from "../contexts/AdminAuthContext";

export default function AdminWhitelist() {
    const { admin } = useAdminAuth();
    const [items, setItems] = useState([]);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = () =>
        api.get("/admin/whitelist")
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]));

    useEffect(() => { refresh(); }, []);

    const add = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        try {
            await api.post("/admin/whitelist", { email: email.trim().toLowerCase() });
            toast.success("Email whitelisted");
            setEmail("");
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to add");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (entry) => {
        if (entry.email === admin?.email) {
            toast.error("You cannot remove your own whitelist entry");
            return;
        }
        if (!window.confirm(`Remove ${entry.email} from the whitelist?`)) return;
        try {
            await api.delete(`/admin/whitelist/${entry.id}`);
            toast.success("Removed");
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to remove");
        }
    };

    return (
        <div data-testid="admin-whitelist-page">
            <span className="label-eyebrow">Access</span>
            <h1 className="mt-2 font-display text-4xl text-inkDark">Admin whitelist</h1>
            <p className="mt-1 text-inkMuted max-w-2xl">
                The source-of-truth list of emails permitted to access this admin console.
                Currently informational \u2014 wired to OAuth in a future iteration.
            </p>

            <form onSubmit={add} className="mt-8 max-w-2xl bg-ivory rounded-2xl border border-inkDark/5 p-6 flex flex-col sm:flex-row gap-3" data-testid="whitelist-add-form">
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="newadmin@sukaanyas.com"
                    className="flex-1 rounded-xl border border-inkDark/10 bg-white px-4 py-2.5 outline-none focus:border-skyblueDeep"
                    data-testid="whitelist-email-input"
                />
                <button type="submit" disabled={busy} className="btn-pill text-sm disabled:opacity-50" data-testid="whitelist-add-button">
                    <Plus size={14} /> Whitelist email
                </button>
            </form>

            <div className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden max-w-3xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="whitelist-table">
                        <thead className="bg-beige text-inkMuted">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium">Email</th>
                                <th className="text-left px-4 py-3 font-medium">Added by</th>
                                <th className="text-left px-4 py-3 font-medium">Added at</th>
                                <th className="text-right px-4 py-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => {
                                const self = e.email === admin?.email;
                                return (
                                    <tr key={e.id} className="border-t border-inkDark/5">
                                        <td className="px-4 py-3 text-inkDark font-medium flex items-center gap-2">
                                            {e.email}
                                            {self ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-skyblue/40 inline-flex items-center gap-1"><ShieldCheck size={10} /> you</span> : null}
                                        </td>
                                        <td className="px-4 py-3 text-inkMuted">{e.addedBy || "—"}</td>
                                        <td className="px-4 py-3 text-inkMuted">{e.addedAt ? new Date(e.addedAt).toLocaleDateString() : "—"}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => remove(e)}
                                                disabled={self || items.length <= 1}
                                                className="p-2 rounded-lg hover:bg-red-50 text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                                title={self ? "You cannot remove yourself" : items.length <= 1 ? "At least one entry must remain" : "Remove"}
                                                data-testid={`whitelist-delete-${e.id}`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 ? (
                                <tr><td colSpan={4} className="px-4 py-10 text-center text-inkMuted">No whitelist entries yet.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
